import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../config";
import { useParams, useNavigate } from "react-router-dom";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { Upload, AlertCircle, Save, ArrowLeft, Image, Package, } from "lucide-react";

const EditProductForm = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [product, setProduct] = useState({
    productName: "",
    productDescription: "",
    productPrice: "",
    productCategory: "",
    productType: 1,
    productQuantity: 1,
  });

  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const fetchProductData = useCallback(async () => {
    try {
      // Fetch product details
      const response = await fetch(
        `${API_BASE_URL}/api/products/details-edit/${productId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const productData = await response.json();
      setProduct({
        productName: productData.productName,
        productDescription: productData.description,
        productPrice: productData.price,
        productCategory: productData.categoryId,
        productType: productData.type,
        productQuantity: productData.quantity,
      });

      // Fetch product images
      const imageResponse = await fetch(
        `${API_BASE_URL}/api/product-images/product/${productId}`
      );
      if (imageResponse.ok) {
        const imageData = await imageResponse.json();
        setExistingImages(imageData);
      }

    } catch (error) {
      console.error("Error fetching product data:", error);
      setError("ไม่สามารถดึงข้อมูลสินค้าได้");
    } finally {
      setLoading(false);
    }
  }, [productId, token]);

  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Use the update-product endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/products/update-product`,
        {
          method: "POST",
          headers: {
            Authorization: `${token}`,
            productId: productId,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            name: product.productName,
            description: product.productDescription,
            price: product.productPrice,
            category: product.productCategory,
            type: product.productType,
            quantity: product.productQuantity,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update product");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate("/account/my-store");
      }, 1500);
    } catch (error) {
      console.error("Error updating product:", error);
      setError("ไม่สามารถอัปเดตสินค้าได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Save className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">บันทึกสำเร็จ!</h2>
          <p className="text-gray-600">กำลังกลับไปยังหน้าร้านค้าของคุณ...</p>
        </div>
      </div>
    );
  }

  const mainImage = existingImages.length > 0 ? existingImages[0].imageUrl : null;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-red-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            ย้อนกลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-900">แก้ไขสินค้า</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Section */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-red-600" />
              รูปภาพสินค้า
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">รูปภาพปัจจุบัน</label>
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                  {mainImage ? (
                    <img
                      src={`${API_BASE_URL}${mainImage}`}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "https://via.placeholder.com/400?text=No+Image"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Package className="w-16 h-16" />
                    </div>
                  )}
                </div>
                {/* Existing Images Thumbnails */}
                {existingImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {existingImages.map((img, index) => (
                      <div
                        key={img.imageId || index}
                        className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200"
                      >
                        <img
                          src={`${API_BASE_URL}${img.imageUrl}`}
                          alt={`Product view ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/100?text=N/A"; }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">อัปโหลดรูปใหม่ (ถ้าต้องการเปลี่ยน)</label>

                <div className="aspect-square bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center">
                  <Upload className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">คลิกเพื่ออัปโหลด</p>
                  <p className="text-xs text-gray-300 mt-1">PNG, JPG (max 5MB)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">ข้อมูลสินค้า</h3>

            <div className="space-y-5">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อสินค้า *</label>
                <input
                  type="text"
                  name="productName"
                  value={product.productName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">รายละเอียดสินค้า</label>
                <textarea
                  name="productDescription"
                  value={product.productDescription}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Price & Quantity */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ราคา (บาท) *</label>
                  <input
                    type="number"
                    name="productPrice"
                    value={product.productPrice}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">จำนวนสินค้า *</label>
                  <input
                    type="number"
                    name="productQuantity"
                    value={product.productQuantity}
                    onChange={handleChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">หมวดหมู่ *</label>
                <select
                  name="productCategory"
                  value={product.productCategory}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">เลือกหมวดหมู่</option>
                  {PRODUCT_CATEGORIES.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={saving}
            className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              <>
                <Save size={20} />
                บันทึกการเปลี่ยนแปลง
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditProductForm;
